import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CreditCard, CheckCircle, AlertCircle, Lock, Calendar, DollarSign } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import BrandLogo from '../components/BrandLogo';
import logger from '../utils/logger';

const BillingPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: status, 2: payment method, 3: subscription
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: ''
  });
  const [subscriptionAmount] = useState(99); // Default monthly price
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();
  const { user, enableTrialPeriod, logout } = useAuth();

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.BILLING}/status`);
      setSubscription(response.data.data);
      
      // Determine current step based on subscription status
      if (!response.data.data.providerCustomerId) {
        setStep(1);
      } else if (!response.data.data.providerPaymentMethodSet) {
        setStep(2);
      } else if (response.data.data.status !== 'ACTIVE') {
        setStep(3);
      }
    } catch (error) {
      logger.error('Error fetching subscription status:', error);
      toast.error('שגיאה בטעינת סטטוס המנוי');
    } finally {
      setLoading(false);
    }
  };

  const createCustomer = async () => {
    try {
      setProcessing(true);
      await axios.post(`${API_ENDPOINTS.BILLING}/create-customer`);
      toast.success('חשבון לקוח נוצר בהצלחה');
      setStep(2);
      await fetchSubscriptionStatus();
    } catch (error) {
      logger.error('Error creating customer:', error);
      toast.error('שגיאה ביצירת חשבון לקוח');
    } finally {
      setProcessing(false);
    }
  };

  const setupPaymentMethod = async () => {
    try {
      setProcessing(true);
      await axios.post(`${API_ENDPOINTS.BILLING}/setup-payment-method`, paymentData);
      toast.success('אמצעי תשלום הוגדר בהצלחה');
      setStep(3);
      await fetchSubscriptionStatus();
    } catch (error) {
      logger.error('Error setting up payment method:', error);
      toast.error('שגיאה בהגדרת אמצעי תשלום');
    } finally {
      setProcessing(false);
    }
  };

  const createSubscription = async () => {
    try {
      setProcessing(true);
      await axios.post(`${API_ENDPOINTS.BILLING}/subscribe`, {
        amount: subscriptionAmount
      });
      toast.success('המנוי הופעל בהצלחה!');
      await fetchSubscriptionStatus();
    } catch (error) {
      logger.error('Error creating subscription:', error);
      toast.error('שגיאה בהפעלת המנוי');
    } finally {
      setProcessing(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm('האם אתה בטוח שברצונך לבטל את המנוי?')) {
      return;
    }

    try {
      setProcessing(true);
      await axios.post(`${API_ENDPOINTS.BILLING}/cancel`);
      toast.success('המנוי בוטל בהצלחה');
      await fetchSubscriptionStatus();
    } catch (error) {
      logger.error('Error cancelling subscription:', error);
      toast.error('שגיאה בביטול המנוי');
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/driver/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-brown-700 text-lg">טוען...</p>
        </div>
      </div>
    );
  }

  const getStatusInfo = () => {
    // If trial period is disabled, show free service message
    if (!enableTrialPeriod) {
      return {
        icon: <CheckCircle className="h-8 w-8 text-green-500" />,
        title: 'שירות חינמי',
        description: 'השירות זמין עבורך ללא תשלום',
        color: 'green'
      };
    }

    switch (subscription?.status) {
      case 'TRIAL':
        return {
          icon: <Calendar className="h-8 w-8 text-blue-500" />,
          title: 'תקופת ניסיון',
          description: `נותרו ${subscription.daysLeft} ימים בתקופת הניסיון`,
          color: 'blue'
        };
      case 'ACTIVE':
        return {
          icon: <CheckCircle className="h-8 w-8 text-green-500" />,
          title: 'מנוי פעיל',
          description: 'המנוי שלך פעיל ותקין',
          color: 'green'
        };
      case 'EXPIRED':
        return {
          icon: <AlertCircle className="h-8 w-8 text-red-500" />,
          title: 'מנוי פג תוקף',
          description: 'המנוי שלך פג תוקף - נדרש חידוש',
          color: 'red'
        };
      case 'CANCELED':
        return {
          icon: <Lock className="h-8 w-8 text-gray-500" />,
          title: 'מנוי מבוטל',
          description: 'המנוי שלך בוטל',
          color: 'gray'
        };
      default:
        return {
          icon: <AlertCircle className="h-8 w-8 text-gray-500" />,
          title: 'לא ידוע',
          description: 'סטטוס המנוי לא ידוע',
          color: 'gray'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50">
      {/* Header */}
      <header className="header-gradient shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <BrandLogo className="h-8 md:h-10 w-auto" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mr-2 md:mr-4">ניהול מנוי</h1>
            </div>
            <div className="flex items-center space-x-4 space-x-reverse">
              <span className="hidden sm:inline text-white text-base sm:text-lg md:text-xl">
                שלום, {user?.contactPerson || user?.businessName}
              </span>
              <button
                onClick={handleLogout}
                className="text-white hover:text-orange-100 font-semibold transition-colors duration-300"
              >
                התנתק
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Current Status */}
        <div className="card mb-8">
          <div className="flex items-center space-x-4 space-x-reverse">
            {statusInfo.icon}
            <div>
              <h2 className={`text-2xl font-bold text-${statusInfo.color}-900`}>
                {statusInfo.title}
              </h2>
              <p className={`text-${statusInfo.color}-700`}>
                {statusInfo.description}
              </p>
              {enableTrialPeriod && subscription?.trialEndsAt && (
                <p className="text-sm text-gray-600 mt-1">
                  תאריך סיום: {new Date(subscription.trialEndsAt).toLocaleDateString('he-IL')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Progress Steps - Only show if trial period is enabled */}
        {enableTrialPeriod && (
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step >= stepNum 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-gray-300 text-gray-600'
                }`}>
                  {stepNum}
                </div>
                <div className="mr-2 text-sm text-gray-600">
                  {stepNum === 1 ? 'יצירת לקוח' : stepNum === 2 ? 'אמצעי תשלום' : 'הפעלת מנוי'}
                </div>
                {stepNum < 3 && (
                  <div className={`w-16 h-1 mx-4 ${
                    step > stepNum ? 'bg-orange-500' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Show message if trial period is disabled */}
        {!enableTrialPeriod && (
          <div className="card">
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-green-900 mb-4">שירות חינמי ללא הגבלה</h3>
              <p className="text-brown-700 text-lg">
                השירות זמין עבורך ללא תשלום.
                <br />
                תוכל להשתמש בכל התכונות של המערכת בחינם.
              </p>
            </div>
          </div>
        )}

        {/* Step 1: Create Customer */}
        {enableTrialPeriod && step === 1 && (
          <div className="card">
            <h3 className="text-xl font-bold text-brown-900 mb-6">יצירת חשבון לקוח</h3>
            <p className="text-brown-700 mb-6">
              כדי להתחיל, נצטרך ליצור חשבון לקוח במערכת התשלומים שלנו.
            </p>
            <button
              onClick={createCustomer}
              disabled={processing}
              className="btn-primary flex items-center"
            >
              {processing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2" />
              ) : (
                <CreditCard className="h-4 w-4 ml-2" />
              )}
              צור חשבון לקוח
            </button>
          </div>
        )}

        {/* Step 2: Payment Method */}
        {enableTrialPeriod && step === 2 && (
          <div className="card">
            <h3 className="text-xl font-bold text-brown-900 mb-6">הגדרת אמצעי תשלום</h3>
            <p className="text-brown-700 mb-6">
              אנא הזן את פרטי הכרטיס שלך לתשלום חודשי.
            </p>
            
            <form onSubmit={(e) => { e.preventDefault(); setupPaymentMethod(); }} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label" htmlFor="cardNumber">מספר כרטיס *</label>
                  <input
                    type="text"
                    id="cardNumber"
                    value={paymentData.cardNumber}
                    onChange={(e) => setPaymentData({...paymentData, cardNumber: e.target.value})}
                    className="input-field"
                    placeholder="1234 5678 9012 3456"
                    maxLength="19"
                    required
                  />
                </div>
                
                <div>
                  <label className="form-label" htmlFor="cardholderName">שם בעל הכרטיס *</label>
                  <input
                    type="text"
                    id="cardholderName"
                    value={paymentData.cardholderName}
                    onChange={(e) => setPaymentData({...paymentData, cardholderName: e.target.value})}
                    className="input-field"
                    placeholder="שם מלא"
                    required
                  />
                </div>
                
                <div>
                  <label className="form-label" htmlFor="expiryMonth">חודש תפוגה *</label>
                  <select
                    id="expiryMonth"
                    value={paymentData.expiryMonth}
                    onChange={(e) => setPaymentData({...paymentData, expiryMonth: e.target.value})}
                    className="input-field"
                    required
                  >
                    <option value="">בחר חודש</option>
                    {Array.from({length: 12}, (_, i) => (
                      <option key={i+1} value={i+1}>{i+1}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="form-label" htmlFor="expiryYear">שנת תפוגה *</label>
                  <select
                    id="expiryYear"
                    value={paymentData.expiryYear}
                    onChange={(e) => setPaymentData({...paymentData, expiryYear: e.target.value})}
                    className="input-field"
                    required
                  >
                    <option value="">בחר שנה</option>
                    {Array.from({length: 10}, (_, i) => {
                      const year = new Date().getFullYear() + i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>
                
                <div>
                  <label className="form-label" htmlFor="cvv">CVV *</label>
                  <input
                    type="text"
                    id="cvv"
                    value={paymentData.cvv}
                    onChange={(e) => setPaymentData({...paymentData, cvv: e.target.value})}
                    className="input-field"
                    placeholder="123"
                    maxLength="4"
                    required
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={processing}
                className="btn-primary flex items-center"
              >
                {processing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2" />
                ) : (
                  <CreditCard className="h-4 w-4 ml-2" />
                )}
                הגדר אמצעי תשלום
              </button>
            </form>
          </div>
        )}

        {/* Step 3: Subscription */}
        {enableTrialPeriod && step === 3 && (
          <div className="card">
            <h3 className="text-xl font-bold text-brown-900 mb-6">הפעלת מנוי חודשי</h3>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
              <div className="flex items-center space-x-3 space-x-reverse">
                <DollarSign className="h-6 w-6 text-orange-600" />
                <div>
                  <h4 className="text-lg font-semibold text-orange-900">תוכנית מנוי חודשית</h4>
                  <p className="text-orange-700">₪{subscriptionAmount} לחודש</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4 mb-6">
              <h4 className="font-semibold text-brown-900">מה כלול במנוי:</h4>
              <ul className="space-y-2 text-brown-700">
                <li className="flex items-center space-x-2 space-x-reverse">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>גישה מלאה לכל הבקשות הזמינות</span>
                </li>
                <li className="flex items-center space-x-2 space-x-reverse">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>שליחת הצעות ללא הגבלה</span>
                </li>
                <li className="flex items-center space-x-2 space-x-reverse">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>דשבורד מתקדם עם סטטיסטיקות</span>
                </li>
                <li className="flex items-center space-x-2 space-x-reverse">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>תמיכה טכנית 24/7</span>
                </li>
              </ul>
            </div>
            
            {subscription?.status === 'ACTIVE' ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-semibold">המנוי שלך פעיל!</p>
                  <p className="text-green-700 text-sm">
                    התשלום הבא: {subscription.currentPeriodEnd ? 
                      new Date(subscription.currentPeriodEnd).toLocaleDateString('he-IL') : 
                      'לא זמין'
                    }
                  </p>
                </div>
                <button
                  onClick={cancelSubscription}
                  disabled={processing}
                  className="btn-danger"
                >
                  {processing ? 'מעבד...' : 'בטל מנוי'}
                </button>
              </div>
            ) : (
              <button
                onClick={createSubscription}
                disabled={processing}
                className="btn-primary flex items-center"
              >
                {processing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2" />
                ) : (
                  <CreditCard className="h-4 w-4 ml-2" />
                )}
                הפעל מנוי - ₪{subscriptionAmount}/חודש
              </button>
            )}
          </div>
        )}

        {/* Back to Dashboard */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/driver/dashboard')}
            className="btn-secondary"
          >
            חזור לדשבורד
          </button>
        </div>
      </main>
    </div>
  );
};

export default BillingPage;
